import React, { useState } from "react";
import axiosBackend from "../api/axiosBackend";
import AutocompleteTagInput from "../components/AutocompleteTagInput";

// shadcn
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import { Label } from "components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle, DialogDescription, DialogTrigger } from "components/ui/dialog";
import { Plus } from "lucide-react";

import { Card, CardContent } from "components/ui/card";

function InsertOrderForm({ formId, onCreated, onClose, onSubmittingChange }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [product, setProduct] = useState("");
  const [comments, setComments] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const resetForm = () => {
    setName("");
    setPhone("");
    setProduct("");
    setComments("");
    setSelectedTags([]);
    setErrorMsg("");
  };

  const validate = () => {
    if (!name.trim()) return "שם חובה";
    if (!phone.trim()) return "מספר פלאפון חובה";
    return "";
  };

  const handleAddOrder = async () => {
    const v = validate();
    if (v) {
      setErrorMsg(v);
      return;
    }
    setSubmitting(true);
    onSubmittingChange?.(true);
    setErrorMsg("");
    try {
      const payload = {
        name,
        phone,
        product,
        comments,
        tags: selectedTags.map((t) => t._id),
      };
      const response = await axiosBackend.post("/orders/add", payload);
      // optional callback to parent
      onCreated?.(response.data.data);
      resetForm();
      onClose?.(); // close dialog if provided
    } catch (error) {
      console.error("Error adding order:", error);
      setErrorMsg("אירעה שגיאה בעת יצירת ההזמנה");
    } finally {
      setSubmitting(false);
      onSubmittingChange?.(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleAddOrder();
  };

  return (
    <form id={formId} onSubmit={handleSubmit} className="space-y-4" dir="rtl">
      <div className="grid grid-cols-1 gap-3">
        <div className="space-y-1">
          <Label htmlFor="name">שם</Label>
          <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>

        <div className="space-y-1">
          <Label htmlFor="phone">מספר פלאפון</Label>
          <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>

        <div className="space-y-1">
          <Label htmlFor="product">מוצר</Label>
          <Input id="product" value={product} onChange={(e) => setProduct(e.target.value)} />
        </div>

        <div className="space-y-1">
          <Label htmlFor="comments">הערות</Label>
          <Textarea id="comments" value={comments} onChange={(e) => setComments(e.target.value)} />
        </div>

        <div className="space-y-1">
          <Label>תגים</Label>
          <AutocompleteTagInput selectedTags={selectedTags} setSelectedTags={setSelectedTags} />
        </div>
      </div>

      {errorMsg && <p className="text-sm text-destructive">{errorMsg}</p>}

      {/* When rendered inside a dialog the actions live in the pinned DialogFooter
          instead, wired to this form via the native `form` attribute. */}
      {!formId && (
        <div className="flex justify-end gap-2">
          {onClose && (
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
              ביטול
            </Button>
          )}
          <Button type="submit" disabled={submitting} className="min-w-28">
            {submitting ? "שולח…" : "הוסף הזמנה"}
          </Button>
        </div>
      )}
    </form>
  );
}

/**
 * Full-page usage (keeps your route-based page working)
 */
export default function InsertOrder() {
  return (
    <div dir="rtl">
      <h1 className="mb-4 text-2xl font-bold">הוסף הזמנה חדשה</h1>
      <Card className="max-w-xl">
        <CardContent className="pt-6">
          <InsertOrderForm />
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Elegant dialog usage inside Orders page:
 *
 * <InsertOrderDialog onCreated={(order)=> refetchOrders()} />
 */
export function InsertOrderDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const formId = "insert-order-form";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="icon"
          className="h-10 w-10 rounded-full shadow-md"
          aria-label="הוספת הזמנה"
          title="הוספת הזמנה"
        >
          <Plus className="h-5 w-5" />
        </Button>
      </DialogTrigger>

      <DialogContent dir="rtl">
        <DialogHeader>
          <DialogTitle>הוספת הזמנה</DialogTitle>
          <DialogDescription>מלא את הפרטים הבאים ליצירת הזמנה חדשה.</DialogDescription>
        </DialogHeader>

        <DialogBody>
          <InsertOrderForm
            formId={formId}
            onCreated={onCreated}
            onClose={() => setOpen(false)}
            onSubmittingChange={setSubmitting}
          />
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={submitting}>
            ביטול
          </Button>
          <Button type="submit" form={formId} disabled={submitting} className="min-w-28">
            {submitting ? "שולח…" : "הוסף הזמנה"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

